import './Content.css';
import { Route, Routes, Navigate } from 'react-router-dom';
import HomePage from '../pages/HomePage';
import FuturesScreenPage from '../pages/FuturesScreenPage';
import NotificationSettingsPage from '../pages/NotificationSettingsPage';
import ProfilePage from '../pages/ProfilePage';
import SettingsPage from '../pages/SettingsPage';
import { useState, useEffect, useCallback, useRef } from 'react';
import { fetchSubscriptionData } from '../funcs/subscriptionService';
import { useDispatch } from 'react-redux';
import SpreadHistoryPage from '../pages/SpreadHistoryPage';

const cover = "/images/cover.png";
const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

function getUserFromHash() {
  try {
    const hash = window.location.hash?.slice(1) || '';
    const params = new URLSearchParams(hash);
    const tgWebAppData = params.get('tgWebAppData');
    if (tgWebAppData) {
      const dataParams = new URLSearchParams(tgWebAppData);
      const userStr = dataParams.get('user');
      if (userStr) return JSON.parse(decodeURIComponent(userStr));
    }
  } catch (e) {}
  return null;
}

function reportError(error, tg) {
  try {
    const user = tg?.initDataUnsafe?.user || getUserFromHash();
    const telegramUser = user
      ? `${user.first_name || ''} ${user.last_name || ''} (@${user.username || 'нет'}, id: ${user.id})`
      : 'не определён';
    fetch(`${API_BASE_URL}/report-error`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        error,
        telegramUser,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString()
      })
    }).catch(() => {});
  } catch (e) {}
}

function hideLoader() {
  const initialLoader = document.querySelector('.loading-screen');
  if (initialLoader) {
    initialLoader.classList.add('fade-out');
    setTimeout(() => { initialLoader.style.display = 'none'; }, 300);
  }
}

function Content() {
  const dispatch = useDispatch();
  const [initData, setInitData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [needsFallbackAuth, setNeedsFallbackAuth] = useState(false);
  const [fallbackDeepLink, setFallbackDeepLink] = useState(null);
  const [fallbackAuthToken, setFallbackAuthToken] = useState(null);
  const [fallbackStatus, setFallbackStatus] = useState('idle'); // idle | waiting | authorized | error
  const pollingRef = useRef(null);

  // Запуск fallback auth — генерация токена и deep link
  const startFallbackAuth = useCallback(async () => {
    try {
      setFallbackStatus('idle');
      const res = await fetch(`${API_BASE_URL}/web-auth/init`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: '{}'
      });
      if (!res.ok) throw new Error('init failed');
      const data = await res.json();
      setFallbackAuthToken(data.authToken);
      setFallbackDeepLink(data.deepLink);
    } catch (e) {
      console.error('Fallback auth init error:', e);
      setFallbackStatus('error');
    }
  }, []);

  // Открытие бота и начало polling
  const handleFallbackLogin = useCallback(() => {
    if (!fallbackDeepLink || !fallbackAuthToken) return;

    // Открываем бота (TMA свернётся, не закроется)
    window.open(fallbackDeepLink, '_blank');
    setFallbackStatus('waiting');

    // Polling каждые 2 секунды
    if (pollingRef.current) clearInterval(pollingRef.current);
    pollingRef.current = setInterval(async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/web-auth/check/${fallbackAuthToken}`);
        const data = await res.json();

        if (data.status === 'authorized') {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
          setFallbackStatus('authorized');

          if (data.subscriptionData) {
            dispatch({ type: 'SET_SUBSCRIPTION_DATA', payload: data.subscriptionData });
          }

          // Сохраняем JWT и telegram_id для будущих запросов
          if (data.accessToken) {
            localStorage.setItem('fallback_access_token', data.accessToken);
          }
          if (data.telegram_id) {
            localStorage.setItem('fallback_telegram_id', data.telegram_id);
          }

          setNeedsFallbackAuth(false);
          hideLoader();
        } else if (data.status === 'expired' || data.status === 'not_found') {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
          setFallbackStatus('error');
          // Перегенерируем токен
          startFallbackAuth();
        }
      } catch (e) {
        console.error('Polling error:', e);
      }
    }, 2000);
  }, [fallbackDeepLink, fallbackAuthToken, dispatch, startFallbackAuth]);

  // Очистка polling при unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, []);

  // Основная инициализация
  useEffect(() => {
    let cancelled = false;

    const waitForTelegramSdk = () => {
      return new Promise((resolve) => {
        if (window.Telegram?.WebApp) {
          resolve(window.Telegram.WebApp);
          return;
        }
        let attempts = 0;
        const interval = setInterval(() => {
          attempts++;
          if (window.Telegram?.WebApp) {
            clearInterval(interval);
            resolve(window.Telegram.WebApp);
          } else if (attempts >= 100) { // 10 секунд
            clearInterval(interval);
            resolve(null);
          }
        }, 100);
      });
    };

    const initializeTelegram = async () => {
      try {
        // ?test_fallback=1 — принудительно показать fallback auth для тестирования
        const urlParams = new URLSearchParams(window.location.search);
        const forceFallback = urlParams.get('test_fallback') === '1';

        // Если уже есть сохранённый fallback токен — используем его сразу
        const savedFallbackToken = localStorage.getItem('fallback_access_token');
        if (forceFallback && savedFallbackToken) {
          // Загружаем данные через сохранённый JWT
          const data = await fetchSubscriptionData(null, null, true);
          if (cancelled) return;
          if (data) {
            dispatch({ type: 'SET_SUBSCRIPTION_DATA', payload: data });
            hideLoader();
            return;
          }
          // Если токен протух — удаляем и идём в fallback auth
          localStorage.removeItem('fallback_access_token');
          localStorage.removeItem('fallback_telegram_id');
        }

        const tg = forceFallback ? null : await waitForTelegramSdk();
        if (cancelled) return;

        // Если SDK не загрузился, но есть сохранённый токен — используем его
        if ((!tg || !tg.initData) && savedFallbackToken && !forceFallback) {
          const data = await fetchSubscriptionData(null, null, true);
          if (cancelled) return;
          if (data) {
            dispatch({ type: 'SET_SUBSCRIPTION_DATA', payload: data });
            hideLoader();
            return;
          }
          // Токен невалиден — удаляем
          localStorage.removeItem('fallback_access_token');
          localStorage.removeItem('fallback_telegram_id');
        }

        if (!tg || !tg.initData) {
          if (!tg) {
            reportError('Telegram WebApp SDK не загрузился за 10 секунд → fallback auth', null);
          } else {
            reportError('SDK загрузился, но initData пустой → fallback auth', tg);
          }

          // Показываем приложение с fallback auth
          setNeedsFallbackAuth(true);
          hideLoader();
          startFallbackAuth();
          return;
        }

        const newInitData = tg.initData;
        setInitData(newInitData);
        dispatch({ type: 'SET_INIT_DATA', payload: newInitData });

        if (newInitData) {
          const inviter_code = tg?.initDataUnsafe?.start_param || null;
          const data = await fetchSubscriptionData(inviter_code, newInitData, true);

          if (!data) {
            reportError('fetchSubscriptionData вернул null', tg);
          }

          if (data) {
            dispatch({ type: 'SET_SUBSCRIPTION_DATA', payload: data });
            hideLoader();
          }
        }
      } catch (error) {
        reportError(`Исключение при инициализации: ${error.message}`, window.Telegram?.WebApp);
        console.error("Ошибка при инициализации:", error);
      }
    };

    const handleInvoiceClosed = async (detail) => {
      if (detail.status === 'paid') {
        try {
          setIsLoading(true);
          const tg = window.Telegram.WebApp;
          const inviter_code = tg?.initDataUnsafe?.start_param || null;
          const data = await fetchSubscriptionData(inviter_code, tg.initData, false);
          dispatch({ type: 'SET_SUBSCRIPTION_DATA', payload: data });
          setIsLoading(false);
        } catch (error) {
          console.error("Ошибка при обновлении данных после оплаты:", error);
        }
      }
    };

    if (window.Telegram?.WebApp) {
      window.Telegram.WebApp.onEvent('invoiceClosed', handleInvoiceClosed);
    }

    initializeTelegram();

    return () => {
      cancelled = true;
      if (window.Telegram?.WebApp) {
        window.Telegram.WebApp.offEvent('invoiceClosed', handleInvoiceClosed);
      }
    };
  }, [dispatch, startFallbackAuth]);

  // Fallback auth UI — кнопка "Войти через Telegram"
  const renderFallbackAuth = () => {
    if (!needsFallbackAuth) return null;

    return (
      <div style={{
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        background: '#202020',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 99998,
        padding: '0 30px',
        textAlign: 'center'
      }}>
        <img src="/images/chuvak.png" alt="" style={{ width: 80, height: 80, marginBottom: 20, borderRadius: '50%' }} />
        <h2 style={{ color: '#fff', margin: '0 0 10px', fontSize: '1.3rem' }}>Метод Чувака Scanner</h2>

        {fallbackStatus === 'waiting' ? (
          <>
            <p style={{ color: '#aaa', fontSize: '0.9rem', marginBottom: 20 }}>
              ⏳ Ожидание авторизации...<br/>
              Нажмите <b>START</b> в боте и вернитесь сюда
            </p>
            <div className="loader" style={{ position: 'relative', transform: 'none', width: 40, height: 40, borderWidth: 4 }}></div>
          </>
        ) : fallbackStatus === 'error' ? (
          <>
            <p style={{ color: '#ff6b6b', fontSize: '0.9rem', marginBottom: 20 }}>
              Время ожидания истекло. Попробуйте ещё раз.
            </p>
            <button
              onClick={handleFallbackLogin}
              disabled={!fallbackDeepLink}
              style={{
                background: '#7C1214',
                color: '#fff',
                border: 'none',
                borderRadius: 12,
                padding: '14px 32px',
                fontSize: '1rem',
                fontWeight: 600,
                cursor: 'pointer',
                opacity: fallbackDeepLink ? 1 : 0.5
              }}
            >
              Попробовать снова
            </button>
          </>
        ) : (
          <>
            <p style={{ color: '#aaa', fontSize: '0.9rem', marginBottom: 24 }}>
              Не удалось загрузить приложение автоматически.<br/>
              Войдите через Telegram бота.
            </p>
            <button
              onClick={handleFallbackLogin}
              disabled={!fallbackDeepLink}
              style={{
                background: '#7C1214',
                color: '#fff',
                border: 'none',
                borderRadius: 12,
                padding: '14px 32px',
                fontSize: '1rem',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                opacity: fallbackDeepLink ? 1 : 0.5
              }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69a.2.2 0 00-.05-.18c-.06-.05-.14-.03-.21-.02-.09.02-1.49.95-4.22 2.79-.4.27-.76.41-1.08.4-.36-.01-1.04-.2-1.55-.37-.63-.2-1.12-.31-1.08-.66.02-.18.27-.36.74-.55 2.92-1.27 4.86-2.11 5.83-2.51 2.78-1.16 3.35-1.36 3.73-1.36.08 0 .27.02.39.12.1.08.13.19.14.27-.01.06.01.24 0 .38z"/>
              </svg>
              Войти через Telegram
            </button>
            {!fallbackDeepLink && (
              <p style={{ color: '#666', fontSize: '0.8rem', marginTop: 10 }}>Загрузка...</p>
            )}
          </>
        )}
      </div>
    );
  };

  if (needsFallbackAuth) {
    return renderFallbackAuth();
  }

  return (
    isLoading ? (
      <div className="loading-screen">
        <div className="loader">
        </div>
        <svg className='logo' xmlns="http://www.w3.org/2000/svg" version="1.0" width="1024px" height="1024px" viewBox="0 0 1024 1024" preserveAspectRatio="xMidYMid meet">
          <g fill="#fff">
            <path d="M705.40 725.25 c-6 -1.15 -9.90 -2.95 -14 -6.45 -2 -1.75 -9.40 -9 -16.45 -16.10 -7.05 -7.15 -16.70 -16.65 -21.45 -21.20 -9.50 -9 -19.65 -20.15 -21.80 -23.75 l-1.35 -2.35 5.70 -5.30 c3.15 -2.90 12.90 -12.45 21.70 -21.15 8.80 -8.75 17.70 -17.25 19.75 -18.85 l3.75 -3 2.75 1.40 c3.45 1.80 5.45 3.45 17.50 14.70 13.05 12.15 17.95 16.90 33.15 31.95 15.05 14.95 15.35 15.45 16.65 25.05 1.20 8.75 0.35 15.25 -2.90 22.20 -5.20 11.05 -13 18.40 -23.30 21.95 -3.20 1.10 -4.85 1.35 -9.85 1.50 -3.70 0.05 -7.50 -0.15 -9.85 -0.60z"/>
            <path d="M495.50 664 c-13.90 -0.80 -30.75 -3.95 -47 -8.85 -8.25 -2.50 -25.40 -9.10 -31.55 -12.15 -27 -13.50 -48.10 -29.30 -65.90 -49.25 -7.50 -8.40 -10.20 -11.90 -15.90 -20.50 -9.95 -15.05 -16.75 -27.25 -22.05 -39.75 -2.50 -5.95 -7.05 -20.10 -9.30 -29.10 -2.30 -9.05 -4.65 -24.70 -5.30 -34.65 -0.65 -10.45 -0.65 -30.80 0.05 -39.30 0.75 -9.75 4 -28.35 6.70 -38.45 2.25 -8.40 8.30 -24.65 11.90 -31.75 2.55 -5.15 9.85 -17.75 13.60 -23.50 11.15 -17.15 28.90 -36.20 44.25 -47.55 12.45 -9.25 16.20 -11.75 25.45 -17.30 11.40 -6.80 19.30 -10.75 30.35 -15.05 23.95 -9.40 40.70 -13.35 63.45 -14.95 20.95 -1.55 41.95 -0.10 61.75 4.15 14.15 3 34.30 10.20 48.75 17.30 8.20 4.05 24.70 14.30 31.15 19.35 15.55 12.20 22.25 18.85 34.80 34.55 9.65 12 12.15 15.45 17.15 23.50 6.45 10.40 9.05 15.30 13.35 24.85 10.15 22.75 15.70 42.90 17.90 65.15 0.80 8.25 0.80 33.15 0 40.75 -1.25 11.45 -5.55 32.95 -8.60 42.85 -1.50 4.85 -7.30 19.75 -10.10 25.85 -3.95 8.65 -9.55 18.50 -16.50 28.95 -5.15 7.80 -9.65 13.45 -19.70 24.60 -33.25 37.15 -63.65 55.30 -109.55 65.55 -20.95 4.65 -36.80 5.90 -59.15 4.70z m32.35 -42.75 c11.80 -1.45 17.35 -2.80 34.45 -8.25 9.25 -2.95 21.50 -8.05 27.20 -11.25 7.25 -4.10 18.50 -12.15 28.50 -20.45 8.85 -7.30 22.20 -22.50 28.60 -32.55 8.75 -13.75 15.75 -28.25 20.35 -42.25 4.60 -14.15 6 -20.20 8.40 -36.50 1.20 -8.20 1.20 -26.95 -0.05 -37.75 -2.65 -24.10 -9.90 -45.10 -23.05 -67 -6.80 -11.30 -9.35 -14.70 -18.65 -25.20 -14.05 -15.80 -32 -29.50 -51.10 -39 -19.70 -9.80 -43.90 -15.65 -69.75 -16.85 -22.25 -1.05 -52.85 5.35 -74.30 15.50 -5.60 2.60 -17.90 9.30 -22.20 12.05 -13.20 8.45 -30.25 24.15 -41.45 38.20 -11.60 14.50 -24.10 40.35 -29.20 60.30 -1.95 7.55 -2.35 9.95 -3.60 20.85 -3.15 27.95 1.15 60.35 11.55 86.30 2.95 7.40 10 21.60 13.50 27.20 11.60 18.50 34.90 41.20 52.70 51.40 3.05 1.70 6.20 3.65 7.05 4.30 1.55 1.15 9.30 5.20 9.95 5.20 0.20 0 2.95 1.10 6.15 2.45 19 8 34.40 11.80 55.85 13.70 5.90 0.55 23.55 0.30 29.10 -0.40z"/>
            <path d="M525.95 592.80 c-0.10 -0.10 -7.70 -0.30 -16.80 -0.40 l-16.65 -0.15 -0.10 -10.25 c-0.10 -5.65 -0.10 -13.80 0 -18.10 l0.20 -7.85 -1.20 -0.25 c-13.50 -2.50 -21.80 -5.70 -33.90 -13.05 -8.55 -5.15 -18.80 -13.95 -23.95 -20.45 -3.25 -4.15 -10.50 -16.20 -14.30 -23.80 -1.75 -3.45 -3.85 -8.60 -4.70 -11.50 -0.85 -2.90 -1.75 -5.75 -1.95 -6.40 l-0.35 -1.10 -16.75 -0.20 c-9.20 -0.15 -17.10 -0.35 -17.55 -0.55 -0.70 -0.25 -0.90 -1.65 -1.20 -9.15 -0.40 -8.70 0 -32.35 0.55 -33.20 0.15 -0.25 1 -0.30 1.85 -0.15 0.90 0.15 7.35 0.50 14.35 0.80 7 0.30 14.25 0.65 16.05 0.80 l3.30 0.25 0.30 -4.20 c0.90 -12.05 7.85 -27 18.75 -40.50 4.55 -5.60 16.40 -16.85 20.85 -19.80 4.90 -3.20 19.20 -10.55 24 -12.25 3.90 -1.40 12.45 -3.35 14.65 -3.35 l1.35 0 -0.15 -18.25 -0.20 -18.25 2.40 0.40 c1.35 0.20 9.90 0.35 19 0.35 l16.55 0 0.35 7.55 c0.15 4.15 0.15 12.20 -0.05 17.90 l-0.30 10.30 2.50 0 c7.35 0 26.15 8.70 40.85 18.90 11 7.60 19.85 16.60 27 27.35 l3.30 5 -1.50 0.40 c-1.90 0.50 -8.95 5.45 -14.25 10 -6.35 5.50 -9.25 7.60 -13.25 9.60 -2.05 1 -4.35 2.40 -5.05 3.05 -0.70 0.70 -1.60 1.10 -2 0.95 -0.40 -0.15 -1.80 -1.90 -3.15 -3.90 -8.50 -12.40 -12.60 -16.75 -21 -21.80 -6.55 -3.95 -12.10 -6.20 -19.45 -7.90 -7.80 -1.80 -18.40 -1.90 -25.30 -0.20 -5.55 1.40 -11.45 3.50 -15.55 5.55 -2.95 1.50 -9.95 6.80 -12.75 9.70 -6.40 6.65 -12.85 17.85 -14.95 26.05 -3.95 15.80 -1.05 35.05 7.25 47.35 8.65 12.85 19.25 21.45 30.70 24.90 5.85 1.75 9.90 2.25 18.75 2.25 9.15 0 9.65 -0.05 18.50 -2.85 13.75 -4.30 25.15 -14.35 32.30 -28.50 1.20 -2.45 2.50 -4.40 2.85 -4.40 0.80 0 4.60 2.40 9.70 6.05 2.15 1.55 5.95 4 8.40 5.45 6.10 3.60 16.65 10.60 18.95 12.60 l1.85 1.65 -5.50 7 c-9.80 12.50 -16.50 19.25 -26.45 26.60 -11.30 8.40 -28.55 15.80 -39.70 17.05 l-3.05 0.35 0.10 9 c0 4.95 0.20 13.20 0.35 18.40 l0.35 9.35 -2.50 0 c-1.35 0 -2.60 -0.10 -2.70 -0.20z"/>
            <path d="M311.95 662.80 c-5.65 -2.30 -9.20 -5.40 -11.45 -10.05 -1.05 -2.10 -1.25 -3.25 -1.25 -7.05 0 -4.30 0.10 -4.70 2.10 -8.80 1.65 -3.30 2.90 -5 5.40 -7.35 4.75 -4.40 6.50 -5.20 12.35 -5.45 4.60 -0.20 5.05 -0.15 8.15 1.35 4.20 1.95 8.10 5.90 10.35 10.40 1.50 3.05 1.65 3.80 1.65 7.90 0 4.10 -0.15 4.80 -1.65 7.80 -1.85 3.65 -4.85 7.55 -7.25 9.30 -4.50 3.30 -12.95 4.25 -18.40 1.95z"/>
            <path d="M314.30 290.95 c-3.65 -0.85 -6.75 -3 -9.35 -6.40 -3.60 -4.80 -4.65 -7.80 -4.70 -13.30 0 -5.50 1 -7.95 4.85 -12.05 3 -3.15 4.90 -4.30 9.25 -5.40 6.80 -1.75 13.40 -0.60 18.50 3.25 5.15 3.85 7.35 8.40 7.40 15.20 0 3.30 -0.25 4.50 -1.30 6.75 -1.55 3.35 -5.80 8.10 -8.80 9.85 -4.05 2.35 -10.85 3.25 -15.85 2.10z"/>
            <path d="M691.25 290.10 c-4.75 -2.30 -9.85 -7.95 -11.80 -13.10 -1.50 -3.95 -1.55 -9.50 -0.10 -12.65 1.45 -3.10 6.75 -8.60 9.80 -10.15 6.05 -3.10 12.55 -3.45 17.70 -0.90 7.25 3.55 11.15 9.85 11.15 18.20 0 5.05 -1.80 8.95 -6.20 13.25 -4.10 4 -8.20 5.80 -14.30 6.10 -3.45 0.15 -4.60 0 -6.25 -0.75z"/>
          </g>
        </svg>
        <h1 className="loading-text">Метод Чувака<br/>Scanner</h1>
      </div>
    ) : (
    <div className="Content">
      <img className="HomePageSlide-images_cover" src={cover} alt="pic" />

      <Routes>
        <Route path="/app" element={<HomePage />} />
        <Route path="/" element={<Navigate to="/app" />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/futures-screen" element={<FuturesScreenPage initData={initData}/>} />
        <Route path="/notifications" element={<NotificationSettingsPage initData={initData}/>} />
        <Route path="/profile" element={<ProfilePage initData={initData}/>} />
        <Route path="/pair-analysis" element={<SpreadHistoryPage />} />
        <Route path="/pair-analysis/:config?" element={<SpreadHistoryPage />} />
      </Routes>
    </div>)
  );
}

export default Content;
